// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
export type WorkbookPreviewSheet = {
  name: string;
  rows: string[][];
  columnCount: number;
  groupByColumn?: number;
  warningRowIndexes?: number[];
};

export type WorkbookPreview = {
  sheets: WorkbookPreviewSheet[];
};

export type WorkbookPreviewOptions = {
  hoursPerDayByEmployee?: ReadonlyMap<string, number>;
};

export class WorkbookParseError extends Error {
  constructor(message = "The saved file is not a readable XLSX workbook.") {
    super(message);
    this.name = "WorkbookParseError";
  }
}

const hasZipSignature = (data: ArrayBuffer) => {
  if (data.byteLength < 4) return false;
  const signature = new Uint8Array(data, 0, 4);
  return signature[0] === 0x50
    && signature[1] === 0x4b
    && (
      (signature[2] === 0x03 && signature[3] === 0x04)
      || (signature[2] === 0x05 && signature[3] === 0x06)
      || (signature[2] === 0x07 && signature[3] === 0x08)
    );
};

const displayCellValue = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : String(value);
};

const splitPreviewDateTime = (value: string) => {
  const trimmed = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2}))?/.exec(trimmed);
  if (iso) {
    return {
      date: `${iso[3]}.${iso[2]}.${iso[1]}`,
      time: iso[4] && iso[5] ? `${iso[4].padStart(2, "0")}:${iso[5]}` : "\u2014",
    };
  }

  const european = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[T\s](\d{1,2}):(\d{2}))?/.exec(trimmed);
  if (european) {
    return {
      date: `${european[1]!.padStart(2, "0")}.${european[2]!.padStart(2, "0")}.${european[3]}`,
      time: european[4] && european[5] ? `${european[4].padStart(2, "0")}:${european[5]}` : "\u2014",
    };
  }

  return { date: trimmed, time: "\u2014" };
};

const normalizedHeader = (value: string) => value.trim().toLocaleLowerCase().replace(/\s+/g, " ");

const compactNumber = (value: number) => {
  const rounded = Math.round((value + Math.sign(value) * Number.EPSILON) * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

const previewHoursPerDay = (value: string, employee: string, options: WorkbookPreviewOptions) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("/")) return trimmed;

  const hoursMatch = /^(-?\d+(?:[.,]\d+)?)\s*h?$/i.exec(trimmed);
  const configuredHoursPerDay = options.hoursPerDayByEmployee?.get(employee);
  if (hoursMatch && configuredHoursPerDay && Number.isFinite(configuredHoursPerDay) && configuredHoursPerDay > 0) {
    const hours = Number(hoursMatch[1]!.replace(",", "."));
    return `${compactNumber(hours)} h / ${compactNumber(hours / configuredHoursPerDay)} d`;
  }

  return trimmed && !/[a-z]/i.test(trimmed) ? `${trimmed} h` : trimmed;
};

export const normalizeAbsenceDetailsRows = (rows: string[][], options: WorkbookPreviewOptions = {}) => {
  const nonEmptyRows = rows.filter((row) => row.some((cell) => cell.trim() !== ""));
  const header = nonEmptyRows[0];
  if (!header) return nonEmptyRows;

  const headerIndex = new Map(header.map((value, index) => [normalizedHeader(value), index]));
  const employeeIndex = headerIndex.get("employee");
  const dateIndex = headerIndex.get("date");
  const typeIndex = headerIndex.get("absence type");
  const hoursIndex = headerIndex.get("hours / day") ?? headerIndex.get("hours/day") ?? headerIndex.get("hours");
  if (employeeIndex === undefined || dateIndex === undefined || typeIndex === undefined || hoursIndex === undefined) return nonEmptyRows;

  const timeIndex = headerIndex.get("time");
  return [
    ["Employee", "Date", "Time", "Absence type", "Hours / day"],
    ...nonEmptyRows.slice(1).map((row) => {
      const employee = row[employeeIndex] ?? "";
      const dateTime = splitPreviewDateTime(row[dateIndex] ?? "");
      const explicitTime = timeIndex === undefined ? "" : (row[timeIndex] ?? "").trim();
      const hoursValue = (row[hoursIndex] ?? "").trim();
      return [
        employee,
        dateTime.date,
        explicitTime || dateTime.time,
        row[typeIndex] ?? "",
        previewHoursPerDay(hoursValue, employee, options),
      ];
    }),
  ];
};

export const spreadsheetColumnLabel = (index: number) => {
  let value = index + 1;
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
};

export const parseWorkbookPreview = async (data: ArrayBuffer, options: WorkbookPreviewOptions = {}): Promise<WorkbookPreview> => {
  if (!hasZipSignature(data)) throw new WorkbookParseError();

  try {
    // Lazy loading keeps the spreadsheet parser out of the initial application bundle.
    const { read, utils } = await import("xlsx");
    const workbook = read(data, {
      cellFormula: false,
      cellHTML: false,
      cellText: true,
      cellStyles: true,
    });

    return {
      sheets: workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        if (!worksheet) return { name, rows: [], columnCount: 0 };

        const range = worksheet["!ref"] ? utils.decode_range(worksheet["!ref"]) : null;
        const columnCount = range ? range.e.c - range.s.c + 1 : 0;
        const extractedRows = utils.sheet_to_json<unknown[]>(worksheet, {
          header: 1,
          raw: false,
          defval: "",
          blankrows: true,
        });
        const extractedDisplayRows = extractedRows.map((row) => Array.from(
          { length: columnCount },
          (_value, columnIndex) => displayCellValue(row[columnIndex]),
        ));
        const nonEmptyRows = extractedDisplayRows.map((row, sourceIndex) => ({row, sourceIndex})).filter(({row}) => row.some(cell => cell.trim() !== ""));
        const rows = name === "Absence Details"
          ? normalizeAbsenceDetailsRows(nonEmptyRows.map(({row}) => row), options)
          : extractedDisplayRows;
        // Render the saved export's warning, not a guess from rounded hours or names.
        const warningRowIndexes = name === "Absence Details" ? nonEmptyRows.flatMap(({sourceIndex}, rowIndex) => {
          const cell = worksheet[utils.encode_cell({r: (range?.s.r ?? 0) + sourceIndex, c: range?.s.c ?? 0})];
          const fill = cell?.s?.fgColor?.rgb;
          return rows[rowIndex]?.[3]?.trim().toLowerCase() === "vacation" && typeof fill === "string" && fill.slice(-6).toUpperCase() === "FCE4E4" ? [rowIndex] : [];
        }) : undefined;

        return {
          name,
          rows,
          columnCount: rows.reduce((maximum, row) => Math.max(maximum, row.length), 0),
          ...(name === "Absence Details" && rows.length ? { groupByColumn: 0, warningRowIndexes } : {}),
        };
      }),
    };
  } catch (error) {
    if (error instanceof WorkbookParseError) throw error;
    throw new WorkbookParseError();
  }
};
